import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { SEO } from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SITE_TYPES = [
  'Restaurant',
  'Hospital / Healthcare',
  'Residential Community',
  'Commercial / Office',
  'Educational Institution',
  'Industrial / Manufacturing',
  'Events / Mobile',
  'Other',
];

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    site_type: '', load_requirement: '', message: ''
  });

  useEffect(() => {
    trackEvent(EVENTS.START_RFQ, { source: 'contact_page' });
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      trackEvent(EVENTS.SUBMIT_RFQ, { site_type: form.site_type });
      setSubmitted(true);
      toast.success('Your quote request has been submitted!');
    } catch (err) {
      toast.error('Something went wrong. Please try again or email us at info@voltide.energy');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="rfq-success">
        <div className="text-center max-w-md mx-auto px-4 animate-fade-up">
          <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-3">Thank You!</h1>
          <p className="text-[#57534E] leading-relaxed mb-6">
            Your quote request has been received. Our energy experts will review your requirements and get back to you within 24 hours.
          </p>
          <Button onClick={() => { setSubmitted(false); setForm({ name: '', company: '', email: '', phone: '', site_type: '', load_requirement: '', message: '' }); }}
            variant="outline" className="rounded-full border-[#0061DD] text-[#0061DD]" data-testid="submit-another-btn">
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact"
        path="/contact"
        description="Get a custom BESS quote for your facility. Contact Voltide's energy experts for DG replacement, solar integration, and backup power solutions."
      />
      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-6">
              Contact Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
              Get a <span className="blue-gradient-text">custom quote</span> for your facility
            </h1>
            <p className="text-base lg:text-lg text-[#57534E] leading-relaxed max-w-2xl">
              Tell us about your energy needs and our team will design the optimal BESS solution for you.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="pb-20 lg:pb-24" data-testid="rfq-form-section">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bento-card p-6 lg:p-8">
                <h2 className="text-xl font-bold text-[#1A1A1A] mb-6">Request for Quote</h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="rfq-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-[#1A1A1A]">Full Name *</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={e => handleChange('name', e.target.value)}
                        placeholder="Your full name"
                        className="mt-1.5 h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#0061DD]/20 focus:border-[#0061DD]"
                        data-testid="rfq-name-input"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-sm font-medium text-[#1A1A1A]">Company *</Label>
                      <Input
                        id="company"
                        value={form.company}
                        onChange={e => handleChange('company', e.target.value)}
                        placeholder="Company name"
                        className="mt-1.5 h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#0061DD]/20 focus:border-[#0061DD]"
                        data-testid="rfq-company-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-[#1A1A1A]">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={e => handleChange('email', e.target.value)}
                        placeholder="you@company.com"
                        className="mt-1.5 h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#0061DD]/20 focus:border-[#0061DD]"
                        data-testid="rfq-email-input"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-[#1A1A1A]">Phone</Label>
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={e => handleChange('phone', e.target.value)}
                        placeholder="+91 9876543210"
                        className="mt-1.5 h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#0061DD]/20 focus:border-[#0061DD]"
                        data-testid="rfq-phone-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-[#1A1A1A]">Site Type</Label>
                      <Select value={form.site_type} onValueChange={v => handleChange('site_type', v)}>
                        <SelectTrigger className="mt-1.5 h-12 rounded-xl border-stone-200" data-testid="rfq-site-type-select">
                          <SelectValue placeholder="Select site type" />
                        </SelectTrigger>
                        <SelectContent>
                          {SITE_TYPES.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="load" className="text-sm font-medium text-[#1A1A1A]">Load Requirement</Label>
                      <Input
                        id="load"
                        value={form.load_requirement}
                        onChange={e => handleChange('load_requirement', e.target.value)}
                        placeholder="e.g., 200 KVA"
                        className="mt-1.5 h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#0061DD]/20 focus:border-[#0061DD]"
                        data-testid="rfq-load-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-[#1A1A1A]">Additional Details</Label>
                    <textarea
                      id="message"
                      value={form.message}
                      onChange={e => handleChange('message', e.target.value)}
                      placeholder="Tell us about your energy needs, current setup, or any specific requirements..."
                      rows={4}
                      className="mt-1.5 w-full rounded-xl border border-stone-200 px-4 py-3 text-sm focus:ring-2 focus:ring-[#0061DD]/20 focus:border-[#0061DD] focus:outline-none resize-none"
                      data-testid="rfq-message-textarea"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/20 gap-2 w-full sm:w-auto"
                    data-testid="rfq-submit-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Quote Request'}
                    <Send className="w-5 h-5" />
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bento-card p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <a href="mailto:info@voltide.energy" className="flex items-center gap-3 text-sm text-[#57534E] hover:text-[#0061DD] transition-colors" data-testid="contact-email-link">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-[#0061DD]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">Email</p>
                      <p>info@voltide.energy</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-3 text-sm text-[#57534E]">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-[#0061DD]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">Office</p>
                      <p>WeWork Latitude, Bellary Road, Bengaluru 560024</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bento-card p-6 bg-blue-50/50 border-blue-100">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3">What happens next?</h3>
                <ol className="space-y-3">
                  {[
                    'Our team reviews your requirements',
                    'We design a custom BESS configuration',
                    'You receive a detailed proposal within 48 hours',
                  ].map((step, i) => (
                    <li key={step} className="flex items-start gap-3 text-sm text-[#57534E]">
                      <span className="mono text-xs font-bold text-[#0061DD] bg-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 border border-blue-100">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="bento-card p-6">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3">Quick Links</h3>
                <div className="space-y-2">
                  <a href="/calculator" className="flex items-center gap-2 text-sm text-[#0061DD] hover:underline" data-testid="quick-link-calculator">
                    <ArrowRight className="w-4 h-4" /> Calculate your savings first
                  </a>
                  <a href="/products" className="flex items-center gap-2 text-sm text-[#0061DD] hover:underline" data-testid="quick-link-products">
                    <ArrowRight className="w-4 h-4" /> Browse our products
                  </a>
                  <a href="/case-studies/uru" className="flex items-center gap-2 text-sm text-[#0061DD] hover:underline" data-testid="quick-link-case-study">
                    <ArrowRight className="w-4 h-4" /> Read the Uru Brewpark case study
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
